import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuthStore } from '../store/authStore';
import { Loader2 } from 'lucide-react';
import { UrawaIcon } from '../components/UrawaIcon';
import { warmupBackend } from '../services/api';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [waking, setWaking] = useState(false);
  const wakeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { login, googleLogin, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/app';

  // Pre-warm the (possibly spun-down) backend the moment this page loads, and
  // keep it warm while the user reads the form / completes the Google popup —
  // so by the time we POST credentials the cold start is already done.
  useEffect(() => {
    warmupBackend();
    const id = setInterval(warmupBackend, 8000);
    return () => clearInterval(id);
  }, []);

  // Show an honest "waking up the server" hint if a login is taking long
  // (almost always a cold start). Returns a cleanup that cancels the hint.
  const startSlowHint = () => {
    wakeTimer.current = setTimeout(() => setWaking(true), 4000);
  };
  const stopSlowHint = () => {
    if (wakeTimer.current) clearTimeout(wakeTimer.current);
    wakeTimer.current = null;
    setWaking(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    startSlowHint();

    try {
      await login(email, password);
      navigate(redirect);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      stopSlowHint();
    }
  };

  return (
    <div className="min-h-screen bg-legal-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <UrawaIcon className="h-12 w-12 text-accent" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-legal-900">
          Sign in to your account
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {waking && !error && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              <span>Waking up the server — the first sign-in after a quiet period can take up to a minute. Hang tight…</span>
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-legal-700">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-legal-700">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-legal-300 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-legal-900 bg-accent hover:bg-accent-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-legal-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-legal-500">Or continue with</span>
              </div>
            </div>
            <div className="mt-4 flex justify-center">
              <GoogleLogin
                onSuccess={async (response) => {
                  if (response.credential) {
                    setError('');
                    startSlowHint();
                    try {
                      await googleLogin(response.credential);
                      navigate(redirect);
                    } catch {
                      setError('Google sign-in failed. Please try again.');
                    } finally {
                      stopSlowHint();
                    }
                  }
                }}
                onError={() => setError('Google sign-in failed. Please try again.')}
                theme="outline"
                size="large"
                width="100%"
              />
            </div>
          </div>

          <p className="mt-4 text-center text-sm text-legal-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-accent hover:text-accent-600">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
